/**
 * ReservePreliminaryDialog — the merged "Zarezerwuj wstępnie" action from the
 * offer workspace finalization panel. In one confirmation it calls
 * lock-reservations for the version's accepted nośniki with periods, and
 * release-rejected-units for the version's rejected nośniki, presenting the
 * two halves as separately labelled pre-commit breakdown groups and, after
 * committing, as separately labelled outcome groups — never merged into one
 * count, because both halves use the word "failed" for unrelated reasons.
 *
 * Reuses lock-reservations-dialog.tsx's retired pieces verbatim: the
 * hold-type/expiry picker (`LockStatusPicker`), the lock breakdown
 * (`LockBreakdownView`), and the outcome-row/GRW-identity/error-code
 * rendering shared by every fan-out dialog (`outcome-result-buckets.tsx`).
 * The release half reuses the equivalent `ReleaseBreakdownView`.
 *
 * Traceability: offer-accept-reservation / "Preliminary reservation action
 * in the offer workspace finalization panel".
 */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { parseDateStr, toDateStr } from '@ooh/ui/lib/date-string';
import { addWorkingDays, FALLBACK_SUGGESTION_WORKING_DAYS } from '@ooh/contracts';
import type { LockReservationsResponse, ReleaseRejectedUnitsResponse } from '@ooh/contracts';
import { useSubmitGuard } from '@ooh/ui/lib/use-submit-guard';
import { useLockReservations, useReleaseRejectedUnits } from '../versions/api/mutations';
import {
  useLockPreview,
  useReleaseRejectedPreview,
  useUnitDecisions,
} from '../versions/api/previews';
import type { OutcomeResultUnitLookup } from '../../reservations/components/outcome-result-buckets';
import { LockStatusPicker, type LockStatus } from './lock-status-picker';
import { LockBreakdownView } from './lock-breakdown-view';
import { ReleaseBreakdownView } from './release-breakdown-view';
import { ReserveResultSection, ReleaseResultSection } from './reserve-preliminary-result-view';

const DIALOG_MAX_WIDTH = 'max-w-2xl';

export interface ReservePreliminaryDialogProps {
  open: boolean;
  onClose: () => void;
  offerId: string;
  versionId: number;
  versionNumber: number;
  onNavigateToUnit?: (mediaUnitId: number) => void;
}

/**
 * Each half's own `Promise.allSettled` outcome, kept distinct from the
 * other — a whole-request failure on one half (e.g. a network error, a
 * permission check) SHALL still report in that half's own section, never
 * omitted, so the result never reads as though the failing half had nothing
 * to do or was never attempted (offer-accept-reservation / "the action SHALL
 * report it rather than presenting the reservation half as the whole
 * outcome").
 */
interface CombinedResult {
  lock: PromiseSettledResult<LockReservationsResponse>;
  release: PromiseSettledResult<ReleaseRejectedUnitsResponse>;
}

function computeCanConfirm(
  isPending: boolean,
  ttlDate: Date | undefined,
  lockPreviewLoading: boolean,
  lockPreviewError: boolean,
  releasePreviewLoading: boolean,
  releasePreviewError: boolean,
): boolean {
  return (
    !isPending &&
    ttlDate !== undefined &&
    !lockPreviewLoading &&
    !lockPreviewError &&
    !releasePreviewLoading &&
    !releasePreviewError
  );
}

export function ReservePreliminaryDialog({
  open,
  onClose,
  offerId,
  versionId,
  versionNumber,
  onNavigateToUnit,
}: ReservePreliminaryDialogProps) {
  const { t } = useTranslation('offers');
  const [status, setStatus] = React.useState<LockStatus>('SOFT_RESERVATION');
  const [ttlDateStr, setTtlDateStr] = React.useState(() =>
    toDateStr(addWorkingDays(new Date(), FALLBACK_SUGGESTION_WORKING_DAYS)),
  );
  const [result, setResult] = React.useState<CombinedResult | null>(null);
  const lockReservations = useLockReservations();
  const releaseRejectedUnits = useReleaseRejectedUnits();
  const submitGuard = useSubmitGuard();

  const decisionsQuery = useUnitDecisions(offerId, versionId, { enabled: open });
  const unitLookup = React.useMemo<OutcomeResultUnitLookup>(() => {
    const map: OutcomeResultUnitLookup = new Map();
    for (const item of decisionsQuery.data ?? []) {
      map.set(item.mediaUnitId, item);
    }
    return map;
  }, [decisionsQuery.data]);

  const lockPreview = useLockPreview(offerId, versionId, status, { enabled: open });
  const releasePreview = useReleaseRejectedPreview(offerId, versionId, { enabled: open });

  React.useEffect(() => {
    if (!open) {
      setStatus('SOFT_RESERVATION');
      setTtlDateStr(toDateStr(addWorkingDays(new Date(), FALLBACK_SUGGESTION_WORKING_DAYS)));
      setResult(null);
    }
  }, [open]);

  if (!open) return null;

  const ttlDate = parseDateStr(ttlDateStr);
  const canConfirm = computeCanConfirm(
    lockReservations.isPending || releaseRejectedUnits.isPending,
    ttlDate,
    lockPreview.isLoading,
    lockPreview.isError,
    releasePreview.isLoading,
    releasePreview.isError,
  );

  async function handleConfirm() {
    if (!canConfirm || ttlDate === undefined || !submitGuard.tryStart()) return;
    try {
      const [lockSettled, releaseSettled] = await Promise.allSettled([
        lockReservations.mutateAsync({
          offerId,
          versionId,
          status,
          ttlExpiresAt: toDateStr(ttlDate),
        }),
        releaseRejectedUnits.mutateAsync({ offerId, versionId }),
      ]);
      setResult({ lock: lockSettled, release: releaseSettled });
    } finally {
      submitGuard.finish();
    }
  }

  return (
    <ModalDialog
      onClose={onClose}
      maxWidth={DIALOG_MAX_WIDTH}
      testId="reserve-preliminary-dialog"
      header={
        <h2 className="text-base font-semibold">
          {t('workspace.header.reservePreliminaryDialog.title', { number: versionNumber })}
        </h2>
      }
      footer={
        result == null ? (
          <>
            <Button variant="outline" onClick={onClose}>
              {t('workspace.header.reservePreliminaryDialog.cancelBtn')}
            </Button>
            <Button
              disabled={!canConfirm}
              onClick={() => void handleConfirm()}
              data-testid="reserve-preliminary-confirm-button"
            >
              {t('workspace.header.reservePreliminaryDialog.confirmBtn')}
            </Button>
          </>
        ) : (
          <Button onClick={onClose}>
            {t('workspace.header.reservePreliminaryDialog.closeBtn')}
          </Button>
        )
      }
    >
      {result == null ? (
        <div className="space-y-5">
          <LockStatusPicker
            status={status}
            onStatusChange={setStatus}
            ttlDateStr={ttlDateStr}
            onTtlDateChange={setTtlDateStr}
          />
          <div className="space-y-2" data-testid="reserve-preliminary-reserve-group">
            <h3 className="text-sm font-semibold">
              {t('workspace.header.reservePreliminaryDialog.reserveGroupLabel')}
            </h3>
            <LockBreakdownView preview={lockPreview} lookup={unitLookup} />
          </div>
          <div className="space-y-2" data-testid="reserve-preliminary-release-group">
            <h3 className="text-sm font-semibold">
              {t('workspace.header.reservePreliminaryDialog.releaseGroupLabel')}
            </h3>
            <ReleaseBreakdownView preview={releasePreview} lookup={unitLookup} />
          </div>
        </div>
      ) : (
        <div className="space-y-5">
          <ReserveResultSection
            result={result.lock}
            lookup={unitLookup}
            onNavigateToUnit={onNavigateToUnit}
          />
          <ReleaseResultSection result={result.release} lookup={unitLookup} />
        </div>
      )}
    </ModalDialog>
  );
}
